import { useTranslation } from '@client-app/i18n';
import { VerifyAuthResponse, WorkspacePlan } from '@common/api/authService';
import { billingService, GetInvoicesResponse, GetUserPointsHistoryResponse, SubscriptionSummaryCardState, UserPoints, WorkspaceSubscriptionSummary } from '@common/api/billingService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildUserBillingPlanText, resolveUserBillingIsTrial } from './userBillingPlanText';

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const getFirstDefined = <T = any>(source: any, keys: string[]): T | undefined => {
  for (const key of keys) {
    if (source && source[key] !== undefined && source[key] !== null) {
      return source[key] as T;
    }
  }
  return undefined;
};

const normalizeSummaryCards = (rawCards: any): SubscriptionSummaryCardState[] => {
  if (!rawCards) return [];
  if (Array.isArray(rawCards)) {
    return rawCards.map((card) => ({
      planCode: getFirstDefined<string>(card, ['planCode', 'plan_code']),
      billingCycle: getFirstDefined<'monthly' | 'yearly'>(card, ['billingCycle', 'billing_cycle']),
      buttonText: getFirstDefined<string>(card, ['buttonText', 'button_text', 'actionLabel', 'action_label']),
      disabled: Boolean(getFirstDefined<boolean>(card, ['disabled', 'isDisabled', 'is_disabled'])),
      action: getFirstDefined<string>(card, ['action', 'actionType', 'action_type']),
    }));
  }
  if (typeof rawCards === 'object') {
    return Object.values(rawCards).flatMap((card: any) => normalizeSummaryCards([card]));
  }
  return [];
};

const normalizeWorkspaceSubscriptionSummary = (raw: any): WorkspaceSubscriptionSummary | null => {
  if (!raw || typeof raw !== 'object') return null;
  const cards = normalizeSummaryCards(getFirstDefined<any>(raw, ['cards', 'pricingCards', 'pricing_cards']));
  const rawIsTrial = getFirstDefined<boolean>(raw, ['isTrial', 'is_trial']);

  return {
    title: getFirstDefined<string>(raw, ['title']),
    badge: getFirstDefined<string>(raw, ['badge']),
    supportingText: getFirstDefined<string>(raw, ['supportingText', 'supporting_text']),
    provider: getFirstDefined<string | null>(raw, ['provider']) || null,
    billingProvider: getFirstDefined<string | null>(raw, ['billingProvider', 'billing_provider']) || null,
    paymentProvider: getFirstDefined<string | null>(raw, ['paymentProvider', 'payment_provider']) || null,
    paymentStatus: getFirstDefined<string | null>(raw, ['paymentStatus', 'payment_status']) || null,
    payment_status: getFirstDefined<string | null>(raw, ['payment_status', 'paymentStatus']) || null,
    shopifyPaymentStatus: getFirstDefined<string | null>(raw, ['shopifyPaymentStatus', 'shopify_payment_status']) || null,
    shopify_payment_status: getFirstDefined<string | null>(raw, ['shopify_payment_status', 'shopifyPaymentStatus']) || null,
    paymentPending: getFirstDefined<boolean | null>(raw, ['paymentPending', 'payment_pending']) ?? null,
    payment_pending: getFirstDefined<boolean | null>(raw, ['payment_pending', 'paymentPending']) ?? null,
    isPaymentPending: getFirstDefined<boolean | null>(raw, ['isPaymentPending', 'is_payment_pending']) ?? null,
    is_payment_pending: getFirstDefined<boolean | null>(raw, ['is_payment_pending', 'isPaymentPending']) ?? null,
    shopifyPaymentPending: getFirstDefined<boolean | null>(raw, ['shopifyPaymentPending', 'shopify_payment_pending']) ?? null,
    shopify_payment_pending: getFirstDefined<boolean | null>(raw, ['shopify_payment_pending', 'shopifyPaymentPending']) ?? null,
    reasonCode: getFirstDefined<string | null>(raw, ['reasonCode', 'reason_code']) || null,
    reason_code: getFirstDefined<string | null>(raw, ['reason_code', 'reasonCode']) || null,
    planCreditRefreshCadence: getFirstDefined<string | null>(raw, ['planCreditRefreshCadence', 'plan_credit_refresh_cadence']) || null,
    plan_credit_refresh_cadence: getFirstDefined<string | null>(raw, ['plan_credit_refresh_cadence', 'planCreditRefreshCadence']) || null,
    planCode: getFirstDefined<string>(raw, ['planCode', 'plan_code', 'workspacePlanCode', 'workspace_plan_code']),
    planName: getFirstDefined<string>(raw, ['planName', 'plan_name', 'workspacePlanLabel', 'workspace_plan_label']),
    billingCycle: getFirstDefined<'monthly' | 'yearly'>(raw, ['billingCycle', 'billing_cycle']),
    status: getFirstDefined<string>(raw, ['status', 'workspacePlanStatus', 'workspace_plan_status']),
    isTrial: rawIsTrial === undefined ? undefined : Boolean(rawIsTrial),
    isAutoRenew: getFirstDefined<boolean>(raw, ['isAutoRenew', 'is_auto_renew']),
    cancelAtPeriodEnd: getFirstDefined<boolean | null>(raw, ['cancelAtPeriodEnd', 'cancel_at_period_end']) ?? null,
    cancel_at_period_end: getFirstDefined<boolean | null>(raw, ['cancel_at_period_end', 'cancelAtPeriodEnd']) ?? null,
    nextBillingAt: getFirstDefined<string | null>(raw, ['nextBillingAt', 'next_billing_at']) || null,
    expiresAt: getFirstDefined<string | null>(raw, ['expiresAt', 'expires_at']) || null,
    actionLabel: getFirstDefined<string>(raw, ['actionLabel', 'action_label', 'primaryAction', 'primary_action']),
    primaryAction: getFirstDefined<string>(raw, ['primaryAction', 'primary_action']),
    primaryActionMode: getFirstDefined<string>(raw, ['primaryActionMode', 'primary_action_mode']),
    statusLabel: getFirstDefined<string>(raw, ['statusLabel', 'status_label']),
    statusMessage: getFirstDefined<string>(raw, ['statusMessage', 'status_message', 'supportingText', 'supporting_text']),
    displayBudgetMicrousd: toNumberOrNull(getFirstDefined(raw, ['displayBudgetMicrousd', 'display_budget_microusd'])),
    displayUsedMicrousd: toNumberOrNull(getFirstDefined(raw, ['displayUsedMicrousd', 'display_used_microusd'])),
    displayRemainingMicrousd: toNumberOrNull(getFirstDefined(raw, ['displayRemainingMicrousd', 'display_remaining_microusd'])),
    usageStatus: getFirstDefined<string>(raw, ['usageStatus', 'usage_status']) || null,
    bannerType: getFirstDefined<string>(raw, ['bannerType', 'banner_type']) || null,
    bannerTitle: getFirstDefined<string>(raw, ['bannerTitle', 'banner_title']) || null,
    bannerDescription: getFirstDefined<string>(raw, ['bannerDescription', 'banner_description']) || null,
    bannerAction: getFirstDefined<string>(raw, ['bannerAction', 'banner_action']) || null,
    cards,
  };
};

export interface UserBilling {
  userPlan: WorkspacePlan | null;
  summary: WorkspaceSubscriptionSummary | null;
  userPoints: UserPoints | null;
  pointsHistory: GetUserPointsHistoryResponse | null;
  invoices: GetInvoicesResponse | null;
  invoicesWorkspaceId: string | null;
  loading: {
    plan: boolean;
    points: boolean;
    history: boolean;
    invoices: boolean;
  };
  error: UserBillingError;
  applyWorkspacePlan: (workspacePlan?: WorkspacePlan | null) => void;
  fetchSummary: (workspaceId?: string | null) => Promise<void>;
  fetchUserPoints: () => Promise<void>;
  fetchPointsHistory: (page?: number, size?: number) => Promise<void>;
  fetchInvoices: (page?: number, size?: number, workspaceId?: string | null) => Promise<void>;
  refreshAll: () => Promise<void>;
  planText: {
    planDisplayText: string;
    badge: string;
    expirationText: string;
    buttonText: string;
    status: 'trial' | 'active' | 'canceled' | 'expired' | 'none';
  };
  isExpired: boolean;
  isCanceled: boolean;
  isTrial: boolean;
}

export interface UserBillingError {
  plan: string | null;
  points: string | null;
  history: string | null;
  invoices: string | null;
}

export const useUserBilling = (profile: VerifyAuthResponse | null) => {
  const { locale, t } = useTranslation();
  const userPlan = profile?.workspacePlan || null;
  const userPlanRef = useRef<WorkspacePlan | null>(userPlan);
  const profileWorkspaceIdRef = useRef<string | null>(profile?.workspace?.id || null);
  const [appliedUserPlan, setAppliedUserPlan] = useState<WorkspacePlan | null>(userPlan);
  const [summary, setSummary] = useState<WorkspaceSubscriptionSummary | null>(() => normalizeWorkspaceSubscriptionSummary(userPlan));
  const [userPoints, setUserPoints] = useState<UserPoints | null>(null);
  const [pointsHistory, setPointsHistory] = useState<GetUserPointsHistoryResponse | null>(null);
  const [invoices, setInvoices] = useState<GetInvoicesResponse | null>(null);
  const [invoicesWorkspaceId, setInvoicesWorkspaceId] = useState<string | null>(null);
  const [loading, setLoading] = useState({
    plan: false,
    points: false,
    history: false,
    invoices: false,
  });
  const [error, setError] = useState({
    plan: null,
    points: null,
    history: null,
    invoices: null,
  });

  useEffect(() => {
    userPlanRef.current = userPlan;
    profileWorkspaceIdRef.current = profile?.workspace?.id || null;
    setAppliedUserPlan(userPlan);
    setSummary(normalizeWorkspaceSubscriptionSummary(userPlan));
  }, [profile?.workspace?.id, userPlan]);

  const applyWorkspacePlan = useCallback((workspacePlan?: WorkspacePlan | null) => {
    const nextPlan = workspacePlan || null;
    userPlanRef.current = nextPlan;
    setAppliedUserPlan(nextPlan);
    setError((prev) => ({ ...prev, plan: null }));
    setSummary(normalizeWorkspaceSubscriptionSummary(nextPlan));
  }, []);

  const fetchSummary = useCallback(
    async (workspaceId?: string | null) => {
      void workspaceId;
      applyWorkspacePlan(userPlanRef.current);
    },
    [applyWorkspacePlan],
  );

  // Fetch user points
  const fetchUserPoints = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, points: true }));
      setError((prev) => ({ ...prev, points: null }));
      const response = await billingService.getUserPoints();
      setUserPoints(response);
    } catch (err: any) {
      console.error('Failed to fetch user points:', err);
      setError((prev) => ({
        ...prev,
        points: err.message || 'Failed to fetch points',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, points: false }));
    }
  }, []);

  // Fetch points history
  const fetchPointsHistory = useCallback(async (page: number = 1, size: number = 5) => {
    try {
      setLoading((prev) => ({ ...prev, history: true }));
      setError((prev) => ({ ...prev, history: null }));
      const response = await billingService.getUserPointsHistory({
        page,
        size,
      });

      if (page === 1) {
        setPointsHistory(response);
      } else {
        setPointsHistory((prev) => {
          if (!prev) return response;
          return {
            ...response,
            data: [...prev.data, ...response.data],
          };
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch points history:', err);
      setError((prev) => ({
        ...prev,
        history: err.message || 'Failed to fetch history',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, history: false }));
    }
  }, []);

  const fetchInvoices = useCallback(async (page: number = 1, size: number = 5, workspaceId?: string | null) => {
    try {
      setLoading((prev) => ({ ...prev, invoices: true }));
      setError((prev) => ({ ...prev, invoices: null }));
      if (page === 1) {
        setInvoices(null);
        setInvoicesWorkspaceId(null);
      }
      const requestWorkspaceId = typeof window !== 'undefined' ? workspaceId || window.Bustly?.workspace_id || null : workspaceId || null;
      const response = await billingService.getInvoices({ page, size });

      if (page === 1) {
        setInvoices(response);
        setInvoicesWorkspaceId(requestWorkspaceId);
      } else {
        setInvoices((prev) => {
          if (!prev) return response;
          return {
            ...response,
            invoices: [...prev.invoices, ...response.invoices],
          };
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch invoices:', err);
      setError((prev) => ({
        ...prev,
        invoices: err.message || 'Failed to fetch invoices',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, invoices: false }));
    }
  }, []);

  // Refresh all data
  const refreshAll = useCallback(async (): Promise<void> => {
    await Promise.all([
      fetchSummary(),
      // fetchUserPoints(),
      // fetchPointsHistory(),
      fetchInvoices(),
    ]);
  }, [fetchInvoices, fetchSummary]);

  // 计算订阅状态
  const nowTimestamp = new Date().getTime();
  const summaryExpiredAt = summary?.expiresAt ? new Date(summary.expiresAt).getTime() : null;
  const legacyExpired = Boolean(appliedUserPlan?.currentPeriodEnd && nowTimestamp > new Date(appliedUserPlan.currentPeriodEnd).getTime());
  const isExpired = summary?.status === 'expired' || summary?.status === 'canceled' || Boolean(summaryExpiredAt && nowTimestamp > summaryExpiredAt) || legacyExpired;
  const isTrial = resolveUserBillingIsTrial(summary, appliedUserPlan);
  const isCanceled = Boolean(summary?.status === 'pending_cancellation' || appliedUserPlan?.status === 'pending_cancellation' || appliedUserPlan?.cancelAtPeriodEnd || (summary?.isAutoRenew === false && !isExpired && !isTrial));

  const planText = useMemo(() => {
    return buildUserBillingPlanText({
      summary,
      userPlan: appliedUserPlan,
      isExpired,
      isTrial,
      locale,
      t,
    });
  }, [summary, appliedUserPlan, isExpired, isTrial, locale, t]);

  return useMemo(
    () => ({
      userPlan: appliedUserPlan,
      summary,
      // setUserPlan,
      userPoints,
      pointsHistory,
      invoices,
      invoicesWorkspaceId,
      loading,
      error,
      applyWorkspacePlan,
      fetchSummary,
      // fetchUserPlan,
      fetchUserPoints,
      fetchPointsHistory,
      fetchInvoices,
      refreshAll,
      planText,
      isExpired,
      isCanceled,
      isTrial,
    }),
    [appliedUserPlan, applyWorkspacePlan, error, fetchInvoices, fetchPointsHistory, fetchSummary, fetchUserPoints, invoices, invoicesWorkspaceId, isCanceled, isExpired, isTrial, loading, planText, pointsHistory, refreshAll, summary, userPoints],
  );
};
